// app/(app)/reports/stock-value.tsx
import { Button, Input } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { reportsApi } from "@/services/reportsApi";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system/legacy";
import { useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

interface StockProduct {
  slNo: number;
  productId: number;
  productName: string;
  price: string;
  unitSymbol: string;
  quantity: number;
  totalAmount: string;
}

export default function StockValueReportScreen() {
  const { colors, spacing } = useAppTheme();
  const router = useRouter();

  const [products, setProducts] = useState<StockProduct[]>([]);
  const [summary, setSummary] = useState<{
    totalQuantity: string;
    grandTotal: string;
  } | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Debounced search: reset to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchReport = async (page = 1, isRefreshing = false) => {
    if (isRefreshing) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getStockValueReport({
        page,
        limit: pagination.itemsPerPage,
        search: searchQuery.trim() || undefined,
      });
      if (page === 1) {
        setProducts(response.products);
      } else {
        setProducts((prev) => [...prev, ...response.products]);
      }
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || "Failed to load report");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMore = () => {
    if (
      !loading &&
      !refreshing &&
      pagination.currentPage < pagination.totalPages
    ) {
      fetchReport(pagination.currentPage + 1);
    }
  };

  const handleRefresh = () => {
    fetchReport(1, true);
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await reportsApi.downloadStockValueReportPdf({
        search: searchQuery.trim() || undefined,
      });

      if (Platform.OS === "web") {
        // Web: open PDF in a new tab
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        // Native: use FileSystem and Sharing
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `stock_value_report_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (err: any) {
      showAlert("Error", err.message || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: StockProduct;
    index: number;
  }) => (
    <View
      style={[styles.row, index % 2 === 0 ? styles.rowEven : styles.rowOdd]}
    >
      <Text style={[styles.cell, styles.slNo]}>{item.slNo}</Text>
      <Text style={[styles.cell, styles.productName]}>{item.productName}</Text>
      <Text style={[styles.cell, styles.price]}>
        ₹{parseFloat(item.price).toFixed(2)}
      </Text>
      <Text style={[styles.cell, styles.unit]}>{item.unitSymbol}</Text>
      <Text style={[styles.cell, styles.qty]}>{item.quantity}</Text>
      <Text style={[styles.cell, styles.total]}>
        ₹{parseFloat(item.totalAmount).toFixed(2)}
      </Text>
    </View>
  );

  const ListHeader = () => (
    <View style={styles.headerContainer}>
      <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
        <Ionicons name="arrow-back" size={24} color={colors.primary} />
        <Text style={{ color: colors.primary, marginLeft: 4 }}>Back</Text>
      </TouchableOpacity>

      <Text style={styles.title}>Stock Value Report</Text>

      {/* Search Filter */}
      <View style={styles.searchContainer}>
        <Input
          placeholder="Search product by name..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          containerStyle={styles.searchInput}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity
            onPress={handleClearSearch}
            style={styles.clearButton}
          >
            <Ionicons name="close-circle" size={20} color={colors.textLight} />
          </TouchableOpacity>
        )}
      </View>

      {summary && (
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Quantity (all pages):</Text>
            <Text style={styles.summaryValue}>
              {summary.totalQuantity || "0"}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Grand Total Value (all pages):
            </Text>
            <Text style={[styles.summaryValue, styles.grandValue]}>
              ₹{summary ? parseFloat(summary.grandTotal).toFixed(2) : "0.00"}
            </Text>
          </View>
        </View>
      )}

      {/* Table Header */}
      <View style={styles.headerRow}>
        <Text style={[styles.headerCell, styles.slNo]}>SL</Text>
        <Text style={[styles.headerCell, styles.productName]}>Product</Text>
        <Text style={[styles.headerCell, styles.price]}>Price</Text>
        <Text style={[styles.headerCell, styles.unit]}>Unit</Text>
        <Text style={[styles.headerCell, styles.qty]}>Qty</Text>
        <Text style={[styles.headerCell, styles.total]}>Total</Text>
      </View>
    </View>
  );

  const ListFooter = () => {
    if (loading && products.length > 0) {
      return (
        <View style={styles.footerLoader}>
          <ActivityIndicator size="small" color={colors.primary} />
        </View>
      );
    }
    return (
      <View style={styles.footerContainer}>
        <Button
          title="Download PDF"
          onPress={handleDownloadPdf}
          loading={downloading}
          variant="outline"
        />
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    headerContainer: { padding: spacing.lg, paddingBottom: 0 },
    footerContainer: { padding: spacing.lg, marginTop: spacing.sm },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    searchInput: {
      flex: 1,
    },
    clearButton: {
      marginLeft: spacing.sm,
      padding: 4,
    },
    summaryCard: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    summaryLabel: { fontSize: 14, color: colors.textLight },
    summaryValue: { fontSize: 14, fontWeight: "600", color: colors.text },
    grandValue: { fontSize: 16, fontWeight: "700", color: colors.primary },
    headerRow: {
      flexDirection: "row",
      backgroundColor: colors.primary,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.xs,
      borderRadius: 8,
      marginBottom: spacing.sm,
    },
    headerCell: {
      fontWeight: "600",
      color: "#fff",
      fontSize: 12,
    },
    row: {
      flexDirection: "row",
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.xs,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    rowEven: { backgroundColor: colors.background },
    rowOdd: { backgroundColor: colors.background },
    cell: { fontSize: 12, color: colors.text },
    slNo: { width: 40, textAlign: "center" },
    productName: { flex: 2 },
    price: { width: 70, textAlign: "right" },
    unit: { width: 50, textAlign: "center" },
    qty: { width: 50, textAlign: "right" },
    total: { width: 80, textAlign: "right" },
    footerLoader: { paddingVertical: spacing.lg, alignItems: "center" },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
    errorText: {
      textAlign: "center",
      color: colors.danger,
      marginTop: spacing.lg,
    },
  });

  if (error && !products.length) {
    return (
      <View
        style={[
          styles.container,
          { justifyContent: "center", alignItems: "center" },
        ]}
      >
        <Text style={styles.errorText}>{error}</Text>
        <Button
          title="Retry"
          onPress={() => fetchReport(1)}
          variant="primary"
        />
      </View>
    );
  }

  return (
    <FlatList
      style={styles.container}
      data={products}
      keyExtractor={(item) => item.productId.toString()}
      renderItem={renderItem}
      ListHeaderComponent={ListHeader}
      ListFooterComponent={ListFooter}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.primary}
        />
      }
      onEndReached={loadMore}
      onEndReachedThreshold={0.3}
      showsVerticalScrollIndicator
    />
  );
}
